import * as React from 'react';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Box, Chip, FormControl, FormHelperText, InputLabel, Tooltip, Typography } from '@mui/material';
import { useState, useEffect, useMemo } from 'react';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const SELECT_ALL_ID = '__select_all__';
const SELECT_ALL_OPTION = { id: SELECT_ALL_ID, name: 'Select All' };

export default function AutocompleteWithCheckboxes(props) {
  const {data, name, label, labelType, isError, setValue, errorText, value, multiple, grouped, width, disabled, ...rest} = props;

  const removeExtraFields = object => {
   if(object){
    return {
      id: object.id,
      name: object?.name || ""
    }
   }
  }

  const findElementById = id => {
    if(data) {
      return data.find(element => element.id === id) || null;
    }
  }

  const [columns, setColumns] = useState(() => {
    if (Array.isArray(value) && value.length > 0) {
      return value.map(v => removeExtraFields(v)).filter(Boolean);
    }
    return value > 0
      ? multiple ? [removeExtraFields(findElementById(value))].filter(Boolean) : removeExtraFields(findElementById(value))
      : multiple ? [] : null;
  });

  useEffect(() => {
    setValue(name, columns)
  }, [columns]);

  // Re-validate selected columns when the options list changes (e.g. after
  // toggling assigned/all clients — keep only items still present in new data)
  useEffect(() => {
    if (!data) return;
    setColumns(prev => {
      if (!prev) return prev;
      if (Array.isArray(prev)) {
        const next = prev.filter(p => p && data.some(d => d && d.id === p.id));
        return next.length === prev.length ? prev : next;
      }
      return prev && data.some(d => d && d.id === prev.id) ? prev : null;
    });
  }, [data]);

  // Sync internal columns when value prop changes externally (e.g. Formik enableReinitialize)
  // Also clears columns when value is reset to empty (e.g. drawer opening fresh).
  useEffect(() => {
    if (!Array.isArray(value)) return;
    if (value.length === 0) {
      // Value is empty — reset internal state, but ONLY when it isn't already empty. Returning the
      // SAME reference when already empty is critical: this effect and the columns→setValue effect
      // below form a two-way sync, and creating a fresh `[]` here each render ping-ponged with it
      // into an infinite render loop that froze the input whenever the selection was empty (i.e.
      // creating a new service with no client chosen yet).
      setColumns(prev => {
        if (multiple) return Array.isArray(prev) && prev.length === 0 ? prev : [];
        return prev == null ? prev : null;
      });
      return;
    }
    setColumns(prev => {
      if (Array.isArray(prev) && prev.length > 0) return prev;
      return value.map(v => removeExtraFields(v)).filter(Boolean);
    });
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const allSelected = Array.isArray(columns) && data?.length > 0 && columns.length === data.length;

  // Prepend "Select All" for non-grouped multiple selects
  const options = useMemo(() => {
    if (!multiple || grouped) return data || [];
    return [SELECT_ALL_OPTION, ...(data || [])];
  }, [data, multiple, grouped]);

  const handleChange = (_e, newValue, reason) => {
    if (reason === 'clear') {
      setColumns(multiple ? [] : null);
      return;
    }
    if (multiple && Array.isArray(newValue) && newValue.some(v => v.id === SELECT_ALL_ID)) {
      setColumns(allSelected ? [] : [...(data || [])]);
      return;
    }
    if (multiple) {
      setColumns(Array.isArray(newValue) ? newValue.filter(v => v.id !== SELECT_ALL_ID) : []);
    } else {
      setColumns(newValue);
    }
  };

  return (
    <Box sx={{textAlign:'start'}}>
    {label ? 
      <InputLabel id={label}>
        <Typography variant={labelType || "body1"}>{label}</Typography>
      </InputLabel>
    : null }
    <FormControl {...rest} error={isError} style={{width: width || '100%'}}>
      <Autocomplete
        multiple={multiple}
        disabled={disabled}
        groupBy={grouped && ((option) => option?.group)}
        id="checkboxes-tags-demo"
        options={options}
        value={columns}
        disableCloseOnSelect
        getOptionLabel={(option) => option?.name || ''}
        isOptionEqualToValue={(option, val) => {
          if (option?.id === SELECT_ALL_ID) return false;
          return option?.id === val?.id;
        }}
        filterOptions={(opts, state) => {
          if (!multiple || !state.inputValue) return opts;
          return [
            SELECT_ALL_OPTION,
            ...opts.filter(o => o.id !== SELECT_ALL_ID && o.name?.toLowerCase().includes(state.inputValue.toLowerCase())),
          ];
        }}
        onChange={handleChange}
        renderValue={(value, getCustomizedItemProps) => {
          if (value.length === 0) return null;
          if (value.length === 1) {
            return (
              <Chip {...getCustomizedItemProps({ index: 0 })} key={value[0]?.id ?? 0} label={value[0]?.name || ''} size="small" />
            );
          }
          const allNames = value.map(v => v?.name).filter(Boolean).join(', ');
          return (
            <Tooltip
              title={
                <Box component="ul" sx={{ m: 0, pl: 1.5, listStyle: 'none' }}>
                  {value.map((v, i) => (
                    <li key={v?.id ?? i} style={{ padding: '1px 0', fontSize: '12px' }}>{v?.name || ''}</li>
                  ))}
                </Box>
              }
              arrow
              placement="top"
            >
              <Chip label={`${value.length} selected`} size="small" />
            </Tooltip>
          );
        }}
        renderOption={(props, option, { selected }) => {
          if (option.id === SELECT_ALL_ID) {
            return (
              <li {...props} key="select-all">
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={allSelected}
                  indeterminate={Array.isArray(columns) && columns.length > 0 && !allSelected}
                />
                Select All
              </li>
            );
          }
          return (
            <li {...props}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                key={option.id}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option?.name || ''}
            </li>
          );
        }}
        style={{ width: '100%', height: '100%' }}
        slotProps={{ popper: { sx: { zIndex: 1500 } } }}
        renderInput={(params) => (
          <TextField {...params} sx={{ width: '100%', color: 'red' }} />
        )}
      />
      {isError && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>

    </Box>
  );
}
